import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useStoreActions, useStoreState } from '~/store';
import useTranslation from '~/app/hooks/useTranslation';
import usePageTitle from '~/app/hooks/usePageTitle';
import { useAppHeader } from '~/app/modules/appHeader';
import { route } from '~/app/helpers/route';
import { MACHINES_DETAILS_ROUTES } from '~/machines/Details/Router';
import Icon from '~/app/common/Icon';
import AccessControl from '~/accessControl';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import { CommunicationRole, Machine, Project } from '~/types';
import RoleListItem from './RoleListItem';
import { MachineConversation } from './store';
import classes from './style.module.scss';

export type CommunicationComponent = React.FC;

const Communication: CommunicationComponent = () => {
    const { t } = useTranslation('communication');
    const history = useHistory();
    const { setData } = useAppHeader();
    const { id, projectId } = useParams<{ id: Machine['id']; projectId: Project['id'] }>();

    const machine = useStoreState(state => state.machine.data);
    const project = useStoreState(state => state.project.data);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const roles = useStoreState(state => state.communication.roles);
    const conversations = useStoreState(state => state.communication.conversations);
    const fetchRoles = useStoreActions(actions => actions.communication.fetchRoles);
    const fetchConversations = useStoreActions(actions => actions.communication.fetchConversations);
    const resetCommunication = useStoreActions(actions => actions.communication.reset);
    const prepareOpenChat = useStoreActions(actions => actions.communicationChat.prepareOpenChat);

    const [query, setQuery] = useState<string>('');

    usePageTitle(t('pageTitle'));

    useEffect(() => {
        setData({
            title: t('pageTitle'),
            subTitle: `${machine.intern || ''} ${machine.name || ''}`.trim().toUpperCase(),
            backLink: route(MACHINES_DETAILS_ROUTES.details, { id, projectId }),
            showUser: false,
            machineId: '',
            projectId: '',
            showMachineMessages: false,
            isIssueDetailPage: false,
            isServiceDetailPage: false,
            notification: false,
            search: false
        });
    }, [setData, t, machine, id, projectId]);

    useEffect(() => {
        if (netWorkStatus !== 0) {
            return;
        }

        fetchRoles({ machineId: id, projectId });
        fetchConversations(id);

        return () => {
            resetCommunication();
        };
    }, [id, projectId, netWorkStatus, fetchRoles, fetchConversations, resetCommunication]);

    const filteredRoles = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) {
            return roles;
        }
        return roles.filter(role => {
            const roleName = (role.name || '').toLowerCase();
            if (roleName.includes(q)) {
                return true;
            }
            return (role.users || []).some(user => (user.name || '').toLowerCase().includes(q));
        });
    }, [roles, query]);

    const onSelectRole = useCallback((role: CommunicationRole) => {
        if ((role.userCount ?? 0) <= 0) {
            return;
        }
        history.push(route(MACHINES_DETAILS_ROUTES.communicationRoleUsers, {
            id,
            projectId,
            roleId: role.id
        }));
    }, [history, id, projectId]);

    const onSelectConversation = useCallback((conv: MachineConversation) => {
        prepareOpenChat({
            machineId: id,
            projectId,
            chatId: conv.chatId,
            roleId: conv.roleId,
            userIds: conv.userIds,
            recipients: conv.recipients,
            title: `${machine.intern || ''} · ${conv.roleName}`,
            roleName: conv.roleName,
        });
        history.push(route(MACHINES_DETAILS_ROUTES.communicationChat, { id, projectId }));
    }, [history, id, projectId, machine.intern, prepareOpenChat]);

    const machineName = (machine.intern || machine.name || '').toUpperCase();

    const formatDate = useCallback((dateStr: string | null): string => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        const now = new Date();
        const isToday = d.toDateString() === now.toDateString();
        if (isToday) {
            return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
        }
        return `${d.getDate()}.${d.getMonth() + 1}.`;
    }, []);

    return (
        <AccessControl requiredPermissions={PERMISSION_GROUPS.machineRegistration}>
            <div className={classes['communication-page']}>
                <div className={classes['question']}>{t('question')}</div>
                <div className={classes['context']}>
                    {t('context', {
                        machineName: (machine.name || machine.intern || '').toString(),
                        projectNumber: (project.number || '').toString()
                    })}
                </div>
                <div className={classes['search-wrap']}>
                    <div className={classes['search-field']}>
                        <Icon name="search" />
                        <input
                            type="text"
                            placeholder={t('searchPlaceholder')}
                            value={query}
                            onChange={event => setQuery(event.target.value)}
                        />
                    </div>
                </div>

                <div className={classes['section-header']}>{t('rolesHeader')}</div>
                <div>
                    {filteredRoles.map(role => (
                        <RoleListItem key={role.id} role={role} onSelect={onSelectRole} />
                    ))}
                </div>

                {conversations.length > 0 && (
                    <>
                        <div className={classes['section-header']}>{t('existingConversations')}</div>
                        <div>
                            {conversations.map(conv => (
                                <div
                                    key={conv.chatId}
                                    className={classes['conversation-row']}
                                    onClick={() => onSelectConversation(conv)}
                                >
                                    <div className={classes['conv-avatar']}>
                                        {(conv.roleName || '').substring(0, 2).toUpperCase()}
                                    </div>
                                    <div className={classes['conv-info']}>
                                        <div className={classes['conv-title']}>
                                            {machineName} · {conv.roleName}
                                        </div>
                                        <div className={classes['conv-preview']}>
                                            {conv.lastMessage}
                                        </div>
                                    </div>
                                    <div className={classes['conv-meta']}>
                                        <span className={classes['conv-date']}>
                                            {formatDate(conv.lastMessageAt)}
                                        </span>
                                        {conv.hasUnread && <span className={classes['conv-unread']} />}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </AccessControl>
    );
};

export default Communication;
